import { motion } from 'framer-motion';
import Link from 'next/link';
import React from 'react';
import { ONBOARDING_AMPLITUDE_EVENTS } from '@/templates/Onboarding/constants';
import { Portal } from '@/components/elements/Portal';
import { useTranslationWithLocale } from '@/hooks';
import { AmplitudeService } from '@/services';
import { windowIsReady } from '@/helpers';
import styles from './Menu.module.scss';

interface Item {
	name: string;
	href: string;
	target?: string;
	testId?: string;
}

// Types
interface IProps {
	isOpened: boolean;
	headerElementId?: string;
	data: Item[];
}

// Animation
const menuMotion = {
	initial: 'closed',
	variants: {
		closed: {
			opacity: 0,
			transitionEnd: {
				display: 'none',
			},
		},
		opened: {
			opacity: 1,
			display: 'block',
		},
	},
	transition: {
		duration: 0.25,
	},
	onAnimationStart: (variant: string) => {
		// popup is open
		if (variant === 'opened') {
			document.body.style.overflow = 'hidden';

			return;
		}

		// more than 1 popup is open
		if (document?.querySelectorAll('[role="dialog"]')?.length > 1) {
			document.body.style.overflow = 'hidden';

			return;
		}

		// popup is closed
		document.body.style.overflow = '';
	},
};
const menuListMotion = {
	variants: {
		closed: {
			y: '50%',
			opacity: 0,
		},
		opened: {
			y: 0,
			opacity: 1,
		},
	},
	transition: {
		type: 'spring',
		duration: 0.75,
	},
};

export const Menu = React.forwardRef(
	(
		{ isOpened, headerElementId, data }: IProps,
		ref: React.ForwardedRef<HTMLDivElement>,
	) => {
		// Helpers
		const { t } = useTranslationWithLocale('common');
		const headerHeight = `${
			windowIsReady && headerElementId
				? document.getElementById(headerElementId)?.offsetHeight
				: 0
		}px`;
		const height = `calc(100% - ${headerHeight})`;

		const handleItemClick = (item: Item) => {
			if (item.name === 'links_contactSupport') {
				AmplitudeService.logEvent(
					ONBOARDING_AMPLITUDE_EVENTS.MENU_CONTACT_SUPPORT_CLICKED,
				);
			}
		};

		return (
			<Portal>
				<motion.div
					ref={ref}
					className={`
						${styles['menu']}
					`}
					style={{ height }}
					// Animation
					{...menuMotion}
					animate={isOpened ? 'opened' : 'closed'}
				>
					<motion.div className={styles['menu__body']}>
						<div className='container'>
							<div className={styles['menu__wrapper']}>
								<motion.div
									className={styles['menu__list']}
									style={{
										paddingBottom: headerHeight,
									}}
									// Animation
									{...menuListMotion}
								>
									{data.map(item => (
										<div key={item.name} className={styles['menu__item']}>
											<Link
												target={item.target ?? ''}
												prefetch={false}
												href={item.href}
												className={styles['menu__item-link']}
												onClick={() => handleItemClick(item)}
												data-e2e={item.testId}
											>
												{t(item.name)}
											</Link>
											<div className={styles['menu__item-divider']} />
										</div>
									))}
								</motion.div>
							</div>
						</div>
					</motion.div>
				</motion.div>
			</Portal>
		);
	},
);

export const MemoMenu = React.memo(Menu);
